// Markdown renderer — the AD-0001 §14 plugin chain, hardened per RFC-0010 §2
// (PD-0010 render half — body anchor/image scheme allowlist).
//
// Pipeline (remark → rehype):
//   remark-frontmatter  strip leading `---` YAML so it isn't rendered
//   remark-gfm          tables, task lists, strikethrough, autolinks
//   remark-math         `$…$` / `$$…$$` math nodes (rendered by rehype-katex)
//   remark-emoji        `:tada:` → 🎉
//   ── remark-rehype ──
//   rehypeGuardUrls     strip non-http(s)/scheme-relative href/src (§2)
//   rehype-sanitize     defaultSchema + http(s)-only protocols + math classes
//   rehype-slug         stable heading ids (for the TOC/anchors, Phase 3)
//   rehype-autolink-headings  self-links on headings
//   rehype-highlight    syntax highlighting (skips ```mermaid)
//   rehype-katex        render math from the sanitized math spans
//
// Security ordering: the sanitizer and URL guard run first; slug / autolink /
// highlight / katex run afterward and generate markup with our own trusted code
// from already-sanitized text (the rehype-katex–recommended order). Raw HTML in
// the Markdown source is NOT enabled (no rehype-raw), so a literal `<script>` /
// `<img onerror=…>` in the body is never parsed into an element to begin with.

import { Link } from 'react-router-dom';
import Markdown from 'react-markdown';
import type { Components, Options } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import remarkFrontmatter from 'remark-frontmatter';
import remarkEmoji from 'remark-emoji';
import rehypeSanitize from 'rehype-sanitize';
import rehypeSlug from 'rehype-slug';
import rehypeAutolinkHeadings from 'rehype-autolink-headings';
import rehypeHighlight from 'rehype-highlight';
import rehypeKatex from 'rehype-katex';
import { sanitizeSchema, rehypeGuardUrls } from '@/lib/sanitize-schema';
import { MermaidDiagram } from './mermaid-diagram';

type PluginList = NonNullable<Options['rehypePlugins']>;

const REMARK_PLUGINS: PluginList = [
  remarkFrontmatter,
  remarkGfm,
  remarkMath,
  remarkEmoji,
];

const REHYPE_PLUGINS: PluginList = [
  rehypeGuardUrls,
  [rehypeSanitize, sanitizeSchema],
  rehypeSlug,
  rehypeAutolinkHeadings,
  [rehypeHighlight, { plainText: ['mermaid'], ignoreMissing: true }],
  rehypeKatex,
];

// --- mermaid fenced-block extraction ------------------------------------------

interface MinimalHast {
  type?: string;
  tagName?: string;
  value?: string;
  properties?: { className?: unknown };
  children?: MinimalHast[];
}

/** Recursively concatenate the text content of a HAST node. */
function hastText(node: MinimalHast | undefined): string {
  if (!node) return '';
  if (node.type === 'text' && typeof node.value === 'string') return node.value;
  if (Array.isArray(node.children)) return node.children.map(hastText).join('');
  return '';
}

function classList(className: unknown): string[] {
  if (Array.isArray(className)) return className.map(String);
  if (typeof className === 'string') return className.split(/\s+/);
  return [];
}

/**
 * If a `<pre>` wraps a single ```mermaid code fence, return its raw source;
 * otherwise null. Robust to rehype-highlight having split the code text into
 * spans (text is reconstructed via `hastText`).
 */
function mermaidSourceFromPre(node: MinimalHast | undefined): string | null {
  const code = node?.children?.[0];
  if (!code || code.type !== 'element' || code.tagName !== 'code') return null;
  if (!classList(code.properties?.className).includes('language-mermaid')) {
    return null;
  }
  return hastText(code).replace(/\n$/, '');
}

const preComponent: NonNullable<Components['pre']> = (props) => {
  const { node, children, ...rest } = props;
  const mermaidSource = mermaidSourceFromPre(node as MinimalHast | undefined);
  if (mermaidSource !== null) {
    return <MermaidDiagram chart={mermaidSource} />;
  }
  return <pre {...rest}>{children}</pre>;
};

/**
 * Wrap wide Markdown tables in their own horizontal-scroll container so a wide
 * table scrolls within its box and never forces page-body horizontal scroll
 * (RFC-0014 Phase 3 Task 3.2 — mirrors the existing `.docs-content pre`
 * overflow-x rule; the `.docs-table-wrap` styling lives in index.css).
 */
const tableComponent: NonNullable<Components['table']> = ({ node: _node, children, ...rest }) => (
  <div className="docs-table-wrap">
    <table {...rest}>{children}</table>
  </div>
);

/** An anchor `href` rewritten for rendering, or `null` to drop the link. */
export interface ResolvedRenderHref {
  href: string;
  /** When true, render an in-app react-router `<Link>` (SPA nav, no reload). */
  internal: boolean;
}

interface MarkdownRendererProps {
  content: string;
  className?: string;
  /**
   * Optional anchor rewriter (RFC-0010 §7 `LinkResolver`). Returns the final
   * href + whether it is an in-app route, or `null` to render inert text. When
   * omitted, anchors render as-is (Phase-2 behavior).
   */
  resolveHref?: (href: string) => ResolvedRenderHref | null;
  /**
   * Optional image-src rewriter (RFC-0010 §10 image proxy). Returns the final
   * src, or `null` to drop the image. When omitted, images render as-is.
   */
  resolveImageSrc?: (src: string) => string | null;
}

/**
 * Renders untrusted Markdown from a repository doc body through the hardened
 * AD-0001 §14 pipeline. Wrap in `.docs-content` (or pass `className`) for prose
 * styling. Pass `resolveHref`/`resolveImageSrc` to rewrite in-repo links/images
 * (the reader wires the `LinkResolver`); omit them for a bare render.
 */
export function MarkdownRenderer({
  content,
  className = 'docs-content',
  resolveHref,
  resolveImageSrc,
}: MarkdownRendererProps) {
  const components: Components = { pre: preComponent, table: tableComponent };

  if (resolveHref) {
    components.a = ({ node: _node, href, children, ...rest }) => {
      const resolved = resolveHref(typeof href === 'string' ? href : '');
      if (!resolved) return <span {...rest}>{children}</span>;
      if (resolved.internal) {
        return (
          <Link to={resolved.href} {...rest}>
            {children}
          </Link>
        );
      }
      const isExternal = /^https?:/i.test(resolved.href);
      return (
        <a
          href={resolved.href}
          {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          {...rest}
        >
          {children}
        </a>
      );
    };
  }

  if (resolveImageSrc) {
    components.img = ({ node: _node, src, alt, ...rest }) => {
      const resolved = resolveImageSrc(typeof src === 'string' ? src : '');
      if (!resolved) return null;
      return <img src={resolved} alt={alt ?? ''} {...rest} />;
    };
  }

  return (
    <div className={className}>
      <Markdown
        remarkPlugins={REMARK_PLUGINS}
        rehypePlugins={REHYPE_PLUGINS}
        components={components}
      >
        {content}
      </Markdown>
    </div>
  );
}
